import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import type { LinearWorkspaceConnectionRead } from '../api/resources/models'
import { completeLinearAuthorization } from '../api/resources/delivery'
import type { ApiFailure } from '../api/types'
import { useSession } from '../auth/useSession'
import { takePendingLinearAuthorization } from '../features/delivery/oauthState'
import { Button } from '../ui/Button'
import { LoadingState, ProblemState } from '../ui/states'

interface CallbackParameters {
  readonly state: string | null
  readonly code: string | null
  readonly error: string | null
  readonly errorDescription: string | null
}

type CallbackOutcome =
  | { readonly kind: 'waiting' }
  | { readonly kind: 'missing' }
  | {
      readonly kind: 'success'
      readonly connection: LinearWorkspaceConnectionRead
      readonly workspaceId: string
    }
  | { readonly kind: 'failure'; readonly failure: ApiFailure; readonly workspaceId: string }

function callbackParameters(): CallbackParameters {
  const params = new URLSearchParams(window.location.search)
  return {
    state: params.get('state'),
    code: params.get('code'),
    error: params.get('error'),
    errorDescription: params.get('error_description'),
  }
}

export function LinearOAuthCallback() {
  const session = useSession()
  const navigate = useNavigate()
  const [outcome, setOutcome] = useState<CallbackOutcome>({ kind: 'waiting' })

  const consumed = useRef(false)
  const missing = useRef(false)
  const exchange = useRef<{
    readonly workspaceId: string
    readonly result: ReturnType<typeof completeLinearAuthorization>
  } | null>(null)

  useEffect(() => {
    let ignore = false
    if (session.status === 'loading' || session.status === 'error') return
    if (session.status === 'unauthenticated' || session.status === 'unverified') return

    if (!consumed.current) {
      consumed.current = true
      const callback = callbackParameters()
      if (callback.state === null || callback.state.length === 0) {
        missing.current = true
      } else {
        const pending = takePendingLinearAuthorization(callback.state)
        if (pending === null) {
          missing.current = true
        } else {
          exchange.current = {
            workspaceId: pending.workspaceId,
            result: completeLinearAuthorization(pending.workspaceId, {
              state: callback.state,
              ...(callback.code !== null ? { code: callback.code } : {}),
              ...(callback.error !== null ? { error: callback.error } : {}),
              ...(callback.errorDescription !== null
                ? { error_description: callback.errorDescription }
                : {}),
            }),
          }
        }
      }
    }

    const stripQueryParams = () => void navigate({ replace: true })

    if (missing.current) {
      queueMicrotask(() => {
        if (!ignore) {
          setOutcome({ kind: 'missing' })
          stripQueryParams()
        }
      })
    } else if (exchange.current !== null) {
      const { workspaceId, result } = exchange.current
      void result.then((outcomeResult) => {
        if (ignore) return
        if (outcomeResult.ok) {
          setOutcome({ kind: 'success', connection: outcomeResult.data, workspaceId })
        } else {
          setOutcome({ kind: 'failure', failure: outcomeResult.failure, workspaceId })
        }
        stripQueryParams()
      })
    }

    return () => {
      ignore = true
    }
  }, [session.status, navigate])

  const restart = (workspaceId: string | null) => {
    if (workspaceId === null) {
      void navigate({ to: '/', replace: true })
      return
    }
    void navigate({ href: `/w/${encodeURIComponent(workspaceId)}/settings`, replace: true })
  }

  if (session.status === 'error') {
    return (
      <main className="flex min-h-dvh items-center justify-center px-6 py-8">
        <ProblemState
          error={session.error}
          title="The session could not be checked"
          scale="page"
          onRetry={session.retry}
        />
      </main>
    )
  }

  if (session.status === 'unauthenticated') {
    return (
      <main className="flex min-h-dvh items-center justify-center px-6 py-8">
        <ProblemState
          error={
            new Error(
              'Sign in to your Argus account to finish connecting this Linear workspace.',
            )
          }
          title="Linear needs your Argus session"
          scale="page"
          action={
            <Button
              onClick={() =>
                session.signIn(`${window.location.pathname}${window.location.search}`)
              }
            >
              Sign in to continue
            </Button>
          }
        />
      </main>
    )
  }

  if (session.status === 'unverified') {
    return (
      <main className="flex min-h-dvh items-center justify-center px-6 py-8">
        <ProblemState
          error={
            new Error(
              'Verify your Argus email address, then start the Linear connection again from Settings.',
            )
          }
          title="Your Argus email is not verified yet"
          scale="page"
          action={<Button onClick={() => restart(null)}>Return to workspaces</Button>}
        />
      </main>
    )
  }

  if (outcome.kind === 'missing') {
    return (
      <main className="flex min-h-dvh items-center justify-center px-6 py-8" aria-live="polite">
        <ProblemState
          error={new Error('Start a new Linear connection from your workspace Settings.')}
          title="Linear authorization is not available"
          scale="page"
          action={<Button onClick={() => restart(null)}>Return to workspaces</Button>}
        />
      </main>
    )
  }

  if (outcome.kind === 'failure') {
    return (
      <main className="flex min-h-dvh items-center justify-center px-6 py-8" aria-live="polite">
        <ProblemState
          error={outcome.failure}
          title="Linear connection did not complete"
          scale="page"
          action={
            <Button onClick={() => restart(outcome.workspaceId)}>Return to Settings</Button>
          }
        />
      </main>
    )
  }

  if (outcome.kind === 'success') {
    return (
      <main className="px-8 py-10" aria-live="polite">
        <div className="flex max-w-xl flex-col items-start gap-3">
          <h1 className="font-heading text-xl font-semibold text-foreground">
            Linear connected
          </h1>
          <p className="text-sm text-muted-foreground">
            {outcome.connection.workspace_name} is connected. Review a publication plan before
            Argus creates any delivery intent.
          </p>
          <Button onClick={() => restart(outcome.workspaceId)}>Go to Settings</Button>
        </div>
      </main>
    )
  }

  return (
    <main className="px-8 py-10" aria-live="polite">
      <LoadingState shape="form" rows={2} label="Completing Linear authorization" />
    </main>
  )
}

export const Route = createFileRoute('/integrations/linear/callback')({
  component: LinearOAuthCallback,
})
